import { useState } from "react";
import { useAuth } from "../lib/auth.tsx";
import { recordLegalAcceptance } from "../lib/api.ts";

// Bump this whenever the Terms or Privacy Policy materially changes.
// Existing users with no acceptance OR an older version are re-prompted.
export const CURRENT_LEGAL_VERSION = "2026-05-04";

const TERMS_URL = "https://zedule.app/terms";
const PRIVACY_URL = "https://zedule.app/privacy";

interface Acceptance {
  version: string;
  accepted_at: string;
}

function hasCurrentAcceptance(meta: Record<string, unknown> | undefined): boolean {
  const list = (meta?.legal_acceptances as Acceptance[] | undefined) ?? [];
  return list.some((a) => a?.version === CURRENT_LEGAL_VERSION);
}

export function LegalAcceptanceGate({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  // Skip the gate entirely for the synthetic dev-bypass session — the recorder
  // and local-dev workflow shouldn't be blocked by a modal.
  if (session?.access_token === "dev-bypass") return <>{children}</>;

  const meta = session?.user?.user_metadata as Record<string, unknown> | undefined;
  if (accepted || hasCurrentAcceptance(meta)) return <>{children}</>;

  const submit = async () => {
    if (!terms || !privacy) return;
    setSubmitting(true);
    setError(null);
    try {
      await recordLegalAcceptance({
        version: CURRENT_LEGAL_VERSION,
        terms_url: TERMS_URL,
        privacy_url: PRIVACY_URL,
      });
      setAccepted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-gate-title"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(0,0,0,0.55)",
        zIndex: 100,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          padding: 32,
          background: "var(--paper-raised)",
          borderRadius: 14,
          boxShadow: "var(--shadow-md)",
        }}
      >
        <h2
          id="legal-gate-title"
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: "-0.012em",
            color: "var(--ink)",
            margin: "0 0 8px",
          }}
        >
          One last thing.
        </h2>
        <p style={{ color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.55, margin: "0 0 20px" }}>
          Before you keep going, please confirm you've read our terms and privacy policy.
          We'll record the acceptance against your account.
        </p>

        <label className="flex items-start gap-3" style={{ marginBottom: 12, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: 14, color: "var(--ink)" }}>
            I agree to the{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent-ink)", textDecoration: "underline" }}
            >
              Terms of Service
            </a>
            .
          </span>
        </label>

        <label className="flex items-start gap-3" style={{ marginBottom: 24, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={privacy}
            onChange={(e) => setPrivacy(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: 14, color: "var(--ink)" }}>
            I have read the{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent-ink)", textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {error && (
          <div className="alert-error" style={{ marginBottom: 12, fontSize: 13 }}>
            {error}
          </div>
        )}

        <button
          onClick={submit}
          disabled={!terms || !privacy || submitting}
          className="zd-hero-cta"
          style={{
            width: "100%",
            opacity: !terms || !privacy ? 0.4 : 1,
            cursor: !terms || !privacy || submitting ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Recording…" : "Accept and continue"}
        </button>
      </div>
    </div>
  );
}
